import React, { useEffect, useRef, useState } from "react";
import { Header } from "../../components/Header/Header";
import {
    addDoc, collection, deleteDoc, doc, getDocs, query, setDoc, Timestamp, updateDoc, where, writeBatch,
} from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { Employee, EmployeeSchedule } from "../../types/employee";
import { DateRangePicker } from "../../components/DatePicker/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import weekday from "dayjs/plugin/weekday";
import { useUser } from "../../context/UserContext";
import Snackbar from "@mui/material/Snackbar";
import { Tab, Tabs } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { getScheduleLocationLabel, SCHEDULE_LOCATIONS } from "../../utils/scheduleLocations";

dayjs.extend(weekday);
dayjs.extend(isSameOrBefore);

type ScheduleMap = Record<string, Record<string, EmployeeSchedule>>;

type ShiftTemplate = {
    id: string;
    label: string;
    shortLabel: string;
    location: string;
    startTime: string;
    endTime: string;
    mode: "shift" | "custom" | "erase";
    saved?: boolean;
};

type SavedShiftTemplate = {
    label: string;
    location: string;
    startTime: string;
    endTime: string;
    createdAt: Timestamp;
    createdBy: string | null;
};

type TemplateDraft = {
    label: string;
    location: string;
    startTime: string;
    endTime: string;
} | null;

type ShiftEditor = {
    userId: string;
    date: string;
    location: string;
    startTime: string;
    endTime: string;
    hasExistingShift: boolean;
    chooseEmployee: boolean;
} | null;

type ScheduleView = "mine" | "team" | "week" | "month";

type ViewOption = { id: ScheduleView; label: string };

type PendingOperation =
    | { type: "set"; schedule: EmployeeSchedule }
    | { type: "delete"; userId: string; date: string };

const SHIFT_TEMPLATES: ShiftTemplate[] = [
    { id: "time-off", label: "Time off", shortLabel: "Off", location: "Time Off", startTime: "09:00", endTime: "17:30", mode: "shift" },
    { id: "custom", label: "Custom shift…", shortLabel: "Custom", location: "In Store - Castle", startTime: "09:00", endTime: "17:30", mode: "custom" },
    { id: "erase", label: "Erase shifts", shortLabel: "Erase", location: "", startTime: "", endTime: "", mode: "erase" },
];

const DESKTOP_VIEWS: ViewOption[] = [
    { id: "week", label: "Week" },
    { id: "month", label: "Month" },
];

const MOBILE_VIEWS: ViewOption[] = [
    { id: "mine", label: "My shifts" },
    { id: "team", label: "Team" },
];

const MOBILE_QUERY = "(max-width: 768px)";

const MONTH_WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const LOCATION_LEGEND = [
    { variant: "castle", label: "Castle" },
    { variant: "west-wing", label: "West wing" },
    { variant: "coin-show", label: "Coin show" },
    { variant: "time-off", label: "Time off" },
];

const getDatesInRange = (start: Dayjs | null, end: Dayjs | null): Dayjs[] => {
    if (!start || !end) return [];
    const dates: Dayjs[] = [];
    let current = start.startOf("week").startOf("day");
    while (current.isSameOrBefore(end, "day")) {
        dates.push(current);
        current = current.add(1, "day");
    }
    return dates;
};

const buildSchedule = (
    userId: string,
    date: string,
    location: string,
    startTime: string,
    endTime: string
): EmployeeSchedule => {
    const [year, month, day] = date.split("-").map(Number);
    const [startHours, startMinutes] = startTime.split(":").map(Number);
    const [endHours, endMinutes] = endTime.split(":").map(Number);
    const shiftStart = new Date(year, month - 1, day, startHours, startMinutes);
    let shiftEnd = new Date(year, month - 1, day, endHours, endMinutes);
    if (location !== "Time Off" && shiftEnd <= shiftStart) shiftEnd = dayjs(shiftEnd).add(1, "day").toDate();

    return {
        userId,
        location,
        date,
        shiftStart: Timestamp.fromDate(shiftStart),
        shiftEnd: Timestamp.fromDate(shiftEnd),
    };
};

const formatTime = (timestamp: Timestamp) => timestamp.toDate().toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
});

const formatCompactClock = (time: Dayjs) =>
    `${time.format(time.minute() ? "h:mm" : "h")}${time.format("a").charAt(0)}`;

const formatCompactTime = (timestamp: Timestamp) => formatCompactClock(dayjs(timestamp.toDate()));

// Template times are stored as "HH:mm" strings.
const formatTemplateRange = (startTime: string, endTime: string) =>
    `${formatCompactClock(dayjs(`2000-01-01T${startTime}`))}–${formatCompactClock(dayjs(`2000-01-01T${endTime}`))}`;

const formatCompactRange = (schedule: EmployeeSchedule) =>
    `${formatCompactTime(schedule.shiftStart)}–${formatCompactTime(schedule.shiftEnd)}`;

const getShortLocationLabel = (location: string) =>
    getScheduleLocationLabel(location).replace("In Store - ", "");

const isTimeOffLocation = (location: string) => location === "Time Off" || location === "Pto";

const isTimeOffSchedule = (schedule: EmployeeSchedule) => isTimeOffLocation(schedule.location);

const getLocationVariant = (location: string) => {
    if (isTimeOffLocation(location)) return "time-off";
    if (location === "Coin Show") return "coin-show";
    if (location === "In Store - West wing") return "west-wing";
    return "castle";
};

const TEMPLATE_LOCATIONS = SCHEDULE_LOCATIONS.filter((location) => !isTimeOffLocation(location));

const toShiftTemplate = (id: string, data: SavedShiftTemplate): ShiftTemplate => ({
    id,
    label: data.label,
    shortLabel: data.label,
    location: data.location,
    startTime: data.startTime,
    endTime: data.endTime,
    mode: "shift",
    saved: true,
});

export const Scheduler: React.FC = () => {
    const [selectedTemplateId, setSelectedTemplateId] = useState("custom");
    const [shiftEditor, setShiftEditor] = useState<ShiftEditor>(null);
    const [savedTemplates, setSavedTemplates] = useState<ShiftTemplate[]>([]);
    const [templateDraft, setTemplateDraft] = useState<TemplateDraft>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);
    const [employees, setEmployees] = useState<Employee[]>([]);
    // Kept apart from `employees` so toggling visibility doesn't trigger a schedule refetch.
    const [hiddenEmployeeIds, setHiddenEmployeeIds] = useState<Set<string>>(new Set());
    const [showPeopleEditor, setShowPeopleEditor] = useState(false);
    const [employeeScheduleMap, setEmployeeScheduleMap] = useState<ScheduleMap>({});
    const [message, setMessage] = useState("");
    const [tabIndex, setTabIndex] = useState(0);
    const [scheduleView, setScheduleView] = useState<ScheduleView>(() => window.matchMedia(MOBILE_QUERY).matches ? "mine" : "week");
    const [blackoutDate, setBlackoutDate] = useState("");
    const [blackoutDays, setBlackoutDays] = useState<{ id: string; date: string }[]>([]);
    const [dateRange, setDateRange] = useState<{ startDate: Dayjs | null; endDate: Dayjs | null }>(() => {
        const today = dayjs();
        return { startDate: today.startOf("week"), endDate: today.add(3, "week").endOf("week") };
    });
    const pendingOperationsRef = useRef<Map<string, PendingOperation>>(new Map());
    const paintedCellsRef = useRef<Set<string>>(new Set());
    const isPaintingRef = useRef(false);
    const touchStartRef = useRef<{ userId: string; date: string; x: number; y: number } | null>(null);
    const { userData } = useUser();
    const isMobile = useMediaQuery(MOBILE_QUERY, { noSsr: true });
    const viewOptions = isMobile
        ? [...MOBILE_VIEWS, ...(userData?.admin ? [{ id: "week" as const, label: "Grid" }] : [])]
        : DESKTOP_VIEWS;
    const activeView = viewOptions.some((view) => view.id === scheduleView) ? scheduleView : viewOptions[0].id;

    useEffect(() => {
        const fetchEmployees = async () => {
            try {
                const snapshot = await getDocs(collection(db, "employees"));
                const results = snapshot.docs
                    .map((employeeDoc) => ({ id: employeeDoc.id, ...employeeDoc.data() }) as Employee)
                    .filter((employee) => !employee.archived)
                    .sort((first, second) =>
                        `${first.firstName} ${first.lastName}`.localeCompare(`${second.firstName} ${second.lastName}`)
                    );
                setEmployees(results);
                setHiddenEmployeeIds(new Set(results.filter((employee) => employee.hideFromSchedule).map((employee) => employee.id)));
            } catch (error) {
                console.error("Error fetching employees:", error);
                setMessage("Could not load employees");
            }
        };
        fetchEmployees();
    }, []);

    const fetchBlackoutDays = async () => {
        try {
            const snapshot = await getDocs(collection(db, "blackout_days"));
            const results = snapshot.docs.map((document) => {
                const data = document.data() as { date?: string };
                return { id: document.id, date: data.date || document.id };
            });
            results.sort((first, second) => first.date.localeCompare(second.date));
            setBlackoutDays(results);
        } catch (error) {
            console.error("Error fetching blackout days:", error);
        }
    };

    useEffect(() => { fetchBlackoutDays(); }, []);

    const fetchAllSchedules = async (employeeList: Employee[], startDate: Dayjs, endDate: Dayjs) => {
        setIsLoadingSchedule(true);
        try {
            const schedulesQuery = query(
                collection(db, "schedules"),
                where("date", ">=", startDate.format("YYYY-MM-DD")),
                where("date", "<=", endDate.format("YYYY-MM-DD"))
            );
            const snapshot = await getDocs(schedulesQuery);
            const scheduleMap: ScheduleMap = {};
            employeeList.forEach((employee) => { scheduleMap[employee.id] = {}; });
            snapshot.docs.forEach((scheduleDoc) => {
                const schedule = scheduleDoc.data() as EmployeeSchedule;
                if (!scheduleMap[schedule.userId]) scheduleMap[schedule.userId] = {};
                scheduleMap[schedule.userId][schedule.date] = schedule;
            });
            setEmployeeScheduleMap(scheduleMap);
        } catch (error) {
            console.error("Error fetching schedules:", error);
            setMessage("Could not load the schedule");
        } finally {
            setIsLoadingSchedule(false);
        }
    };

    useEffect(() => {
        if (!userData?.admin) return;
        const fetchSavedTemplates = async () => {
            try {
                const snapshot = await getDocs(collection(db, "shift_templates"));
                const results = snapshot.docs
                    .map((templateDoc) => ({ id: templateDoc.id, data: templateDoc.data() as SavedShiftTemplate }))
                    .sort((first, second) => (first.data.createdAt?.toMillis() ?? 0) - (second.data.createdAt?.toMillis() ?? 0))
                    .map(({ id, data }) => toShiftTemplate(id, data));
                setSavedTemplates(results);
            } catch (error) {
                console.error("Error fetching shift templates:", error);
                setMessage("Could not load saved shift buttons");
            }
        };
        fetchSavedTemplates();
    }, [userData?.admin]);

    useEffect(() => {
        if (employees.length && dateRange.startDate && dateRange.endDate) {
            fetchAllSchedules(employees, dateRange.startDate, dateRange.endDate);
        }
    }, [dateRange.startDate, dateRange.endDate, employees]);

    const shiftTemplates = [
        ...savedTemplates,
        ...SHIFT_TEMPLATES,
    ];
    const quickFillTemplates = shiftTemplates.filter((template) => template.mode === "shift");
    const selectedTemplate = shiftTemplates.find((template) => template.id === selectedTemplateId) || shiftTemplates[0];
    const workDates = getDatesInRange(dateRange.startDate, dateRange.endDate).filter((date) => date.day() !== 0);
    const scheduleWeeks = Array.from(
        { length: Math.ceil(workDates.length / 6) },
        (_, index) => workDates.slice(index * 6, index * 6 + 6)
    );

    const setVisibleSchedules = (schedules: EmployeeSchedule[]) => {
        setEmployeeScheduleMap((currentMap) => ({
            ...schedules.reduce<ScheduleMap>((nextMap, schedule) => {
                nextMap[schedule.userId] = {
                    ...(nextMap[schedule.userId] || {}),
                    [schedule.date]: schedule,
                };
                return nextMap;
            }, { ...currentMap }),
        }));
    };

    const setVisibleSchedule = (schedule: EmployeeSchedule) => setVisibleSchedules([schedule]);

    const removeVisibleSchedule = (userId: string, date: string) => {
        setEmployeeScheduleMap((currentMap) => ({
            ...currentMap,
            [userId]: Object.fromEntries(
                Object.entries(currentMap[userId] || {}).filter(([scheduleDate]) => scheduleDate !== date)
            ),
        }));
    };

    const openShiftEditor = (userId: string, date: string) => {
        const existingShift = employeeScheduleMap[userId]?.[date];
        setShiftEditor({
            userId,
            date,
            location: existingShift?.location === "Pto" ? "Time Off" : existingShift?.location || "In Store - Castle",
            startTime: existingShift ? dayjs(existingShift.shiftStart.toDate()).format("HH:mm") : "09:00",
            endTime: existingShift ? dayjs(existingShift.shiftEnd.toDate()).format("HH:mm") : "17:30",
            hasExistingShift: Boolean(existingShift),
            chooseEmployee: false,
        });
    };

    const openNewShiftEditor = (date: string) => {
        setShiftEditor({
            userId: "",
            date,
            location: "In Store - Castle",
            startTime: "09:00",
            endTime: "17:30",
            hasExistingShift: false,
            chooseEmployee: true,
        });
    };

    const handleSaveTemplate = async () => {
        if (!userData?.admin || !templateDraft) return;
        if (!templateDraft.startTime || !templateDraft.endTime) { setMessage("Choose a start and end time"); return; }
        if (templateDraft.startTime === templateDraft.endTime) { setMessage("Start and end times can't be the same"); return; }
        const data: SavedShiftTemplate = {
            label: templateDraft.label.trim()
                || `${formatTemplateRange(templateDraft.startTime, templateDraft.endTime)} ${getShortLocationLabel(templateDraft.location)}`,
            location: templateDraft.location,
            startTime: templateDraft.startTime,
            endTime: templateDraft.endTime,
            createdAt: Timestamp.now(),
            createdBy: userData.id ?? null,
        };
        setIsSaving(true);
        try {
            const templateRef = await addDoc(collection(db, "shift_templates"), data);
            setSavedTemplates((currentTemplates) => [...currentTemplates, toShiftTemplate(templateRef.id, data)]);
            setSelectedTemplateId(templateRef.id);
            setTemplateDraft(null);
            setMessage("Shift button saved");
        } catch (error) {
            console.error("Error saving shift template:", error);
            setMessage("Could not save the shift button");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteTemplate = async (template: ShiftTemplate) => {
        if (!userData?.admin || !template.saved) return;
        if (!window.confirm(`Remove the "${template.label}" button? Shifts already on the schedule are not changed.`)) return;
        try {
            await deleteDoc(doc(db, "shift_templates", template.id));
            setSavedTemplates((currentTemplates) => currentTemplates.filter((savedTemplate) => savedTemplate.id !== template.id));
            if (selectedTemplateId === template.id) setSelectedTemplateId("custom");
            setMessage("Shift button removed");
        } catch (error) {
            console.error("Error deleting shift template:", error);
            setMessage("Could not remove the shift button");
        }
    };

    const applySelectedTool = (userId: string, date: string) => {
        if (!userData?.admin) return;
        const cellKey = `${userId}_${date}`;
        if (paintedCellsRef.current.has(cellKey)) return;
        paintedCellsRef.current.add(cellKey);

        if (selectedTemplate.mode === "custom") {
            isPaintingRef.current = false;
            openShiftEditor(userId, date);
            return;
        }
        if (selectedTemplate.mode === "erase") {
            if (!employeeScheduleMap[userId]?.[date]) return;
            removeVisibleSchedule(userId, date);
            pendingOperationsRef.current.set(cellKey, { type: "delete", userId, date });
            return;
        }

        const schedule = buildSchedule(
            userId, date, selectedTemplate.location, selectedTemplate.startTime, selectedTemplate.endTime
        );
        setVisibleSchedule(schedule);
        pendingOperationsRef.current.set(cellKey, { type: "set", schedule });
    };

    const flushPaintOperations = async () => {
        const operations = Array.from(pendingOperationsRef.current.values());
        pendingOperationsRef.current.clear();
        if (!operations.length) return;

        setIsSaving(true);
        try {
            const batch = writeBatch(db);
            operations.forEach((operation) => {
                if (operation.type === "set") {
                    batch.set(doc(db, "schedules", `${operation.schedule.userId}_${operation.schedule.date}`), operation.schedule);
                } else {
                    batch.delete(doc(db, "schedules", `${operation.userId}_${operation.date}`));
                }
            });
            await batch.commit();
            setMessage(operations.length === 1 ? "Schedule saved" : `${operations.length} changes saved`);
        } catch (error) {
            console.error("Error painting schedules:", error);
            setMessage("Could not save schedule changes");
            if (dateRange.startDate && dateRange.endDate) {
                await fetchAllSchedules(employees, dateRange.startDate, dateRange.endDate);
            }
        } finally {
            setIsSaving(false);
        }
    };

    const finishPainting = (event: PointerEvent) => {
        const touchStart = touchStartRef.current;
        if (touchStart) {
            const movement = Math.hypot(event.clientX - touchStart.x, event.clientY - touchStart.y);
            if (movement < 10) applySelectedTool(touchStart.userId, touchStart.date);
            touchStartRef.current = null;
        }
        if (isPaintingRef.current || pendingOperationsRef.current.size) flushPaintOperations();
        isPaintingRef.current = false;
        paintedCellsRef.current.clear();
    };

    useEffect(() => {
        window.addEventListener("pointerup", finishPainting);
        window.addEventListener("pointercancel", finishPainting);
        return () => {
            window.removeEventListener("pointerup", finishPainting);
            window.removeEventListener("pointercancel", finishPainting);
        };
    });

    const handleCellPointerDown = (event: React.PointerEvent, userId: string, date: string) => {
        if (!userData?.admin || event.button !== 0) return;
        if (event.pointerType === "mouse") {
            event.preventDefault();
            isPaintingRef.current = true;
            paintedCellsRef.current.clear();
            applySelectedTool(userId, date);
        } else {
            touchStartRef.current = { userId, date, x: event.clientX, y: event.clientY };
        }
    };

    const handleCellPointerEnter = (event: React.PointerEvent, userId: string, date: string) => {
        if (isPaintingRef.current && event.buttons === 1 && selectedTemplate.mode !== "custom") {
            applySelectedTool(userId, date);
        }
    };

    const handleEditorSave = async () => {
        if (!shiftEditor?.userId) { setMessage("Choose an employee"); return; }
        const schedule = buildSchedule(
            shiftEditor.userId,
            shiftEditor.date,
            shiftEditor.location,
            shiftEditor.startTime,
            shiftEditor.endTime
        );
        setIsSaving(true);
        try {
            await setDoc(doc(db, "schedules", `${schedule.userId}_${schedule.date}`), schedule);
            setVisibleSchedule(schedule);
            setShiftEditor(null);
            setMessage("Shift saved");
        } catch (error) {
            console.error("Error saving custom shift:", error);
            setMessage("Could not save the custom shift");
        } finally {
            setIsSaving(false);
        }
    };

    const handleEditorDelete = async () => {
        if (!shiftEditor?.hasExistingShift) return;
        setIsSaving(true);
        try {
            await deleteDoc(doc(db, "schedules", `${shiftEditor.userId}_${shiftEditor.date}`));
            removeVisibleSchedule(shiftEditor.userId, shiftEditor.date);
            setShiftEditor(null);
            setMessage("Shift deleted");
        } catch (error) {
            console.error("Error deleting shift:", error);
            setMessage("Could not delete the shift");
        } finally {
            setIsSaving(false);
        }
    };

    const copyPreviousWeek = async (weekDates: Dayjs[]) => {
        const schedulesToCopy: EmployeeSchedule[] = [];
        employees.forEach((employee) => {
            weekDates.forEach((targetDate) => {
                const sourceDate = targetDate.subtract(1, "week").format("YYYY-MM-DD");
                const sourceSchedule = employeeScheduleMap[employee.id]?.[sourceDate];
                if (!sourceSchedule) return;
                schedulesToCopy.push({
                    ...sourceSchedule,
                    date: targetDate.format("YYYY-MM-DD"),
                    shiftStart: Timestamp.fromDate(dayjs(sourceSchedule.shiftStart.toDate()).add(1, "week").toDate()),
                    shiftEnd: Timestamp.fromDate(dayjs(sourceSchedule.shiftEnd.toDate()).add(1, "week").toDate()),
                });
            });
        });
        if (!schedulesToCopy.length) { setMessage("The previous week has no shifts to copy"); return; }

        const overwritesExisting = schedulesToCopy.some((schedule) => employeeScheduleMap[schedule.userId]?.[schedule.date]);
        if (overwritesExisting && !window.confirm("Some shifts already exist in this week. Replace matching cells with the previous week's shifts?")) return;

        setIsSaving(true);
        try {
            const batch = writeBatch(db);
            schedulesToCopy.forEach((schedule) => {
                batch.set(doc(db, "schedules", `${schedule.userId}_${schedule.date}`), schedule);
            });
            await batch.commit();
            setVisibleSchedules(schedulesToCopy);
            setMessage(`${schedulesToCopy.length} shifts copied`);
        } catch (error) {
            console.error("Error copying week:", error);
            setMessage("Could not copy the previous week");
        } finally {
            setIsSaving(false);
        }
    };

    const getWeekHours = (employeeId: string, weekDates: Dayjs[]) => {
        const minutes = weekDates.reduce((total, date) => {
            const schedule = employeeScheduleMap[employeeId]?.[date.format("YYYY-MM-DD")];
            if (!schedule || isTimeOffSchedule(schedule)) return total;
            return total + (schedule.shiftEnd.toMillis() - schedule.shiftStart.toMillis()) / 60000;
        }, 0);
        const hours = minutes / 60;
        return Number.isInteger(hours) ? `${hours}` : hours.toFixed(1);
    };

    const updateDateRange = (range: { startDate: Dayjs | null; endDate: Dayjs | null }) => {
        if (!range.startDate || !range.endDate) return;
        setDateRange({ startDate: range.startDate.startOf("week"), endDate: range.endDate });
    };

    const handleAddBlackoutDay = async () => {
        if (!userData?.admin || !blackoutDate) { setMessage("Select a date"); return; }
        await setDoc(doc(db, "blackout_days", blackoutDate), {
            date: blackoutDate, createdAt: Timestamp.now(), createdBy: userData.id ?? null,
        });
        setBlackoutDate("");
        setMessage("Blackout day added");
        await fetchBlackoutDays();
    };

    const handleRemoveBlackoutDay = async (date: string) => {
        if (!userData?.admin) return;
        await deleteDoc(doc(db, "blackout_days", date));
        setMessage("Blackout day removed");
        await fetchBlackoutDays();
    };

    const blackoutDaysInRange = blackoutDays.filter((day) => {
        if (!dateRange.startDate || !dateRange.endDate) return true;
        const target = dayjs(day.date);
        return !target.isBefore(dateRange.startDate, "day") && !target.isAfter(dateRange.endDate, "day");
    });

    const getCellClassName = (schedule?: EmployeeSchedule) =>
        schedule ? `schedule-cell ${getLocationVariant(schedule.location)}` : "schedule-cell empty-cell";

    const getDayShifts = (dateString: string) => employees
        .flatMap((employee) => {
            const schedule = employeeScheduleMap[employee.id]?.[dateString];
            return schedule ? [{ employee, schedule }] : [];
        })
        .sort((first, second) =>
            Number(isTimeOffSchedule(first.schedule)) - Number(isTimeOffSchedule(second.schedule))
            || first.schedule.shiftStart.toMillis() - second.schedule.shiftStart.toMillis()
        );

    const renderMobileRow = (key: string, className: string, userId: string, date: string, content: React.ReactNode) => (
        userData?.admin && userId ? (
            <button key={key} type="button" className={className} onClick={() => openShiftEditor(userId, date)}>{content}</button>
        ) : <div key={key} className={className}>{content}</div>
    );

    const renderMyWeek = (weekDates: Dayjs[]) => (
        <div className="mobile-shift-list">
            {weekDates.map((date) => {
                const dateString = date.format("YYYY-MM-DD");
                const schedule = userData?.id ? employeeScheduleMap[userData.id]?.[dateString] : undefined;
                const isToday = date.isSame(dayjs(), "day");
                const className = `mobile-shift-row ${schedule ? getLocationVariant(schedule.location) : "off"}${isToday ? " today" : ""}`;
                return renderMobileRow(dateString, className, userData?.id ?? "", dateString, (
                    <>
                        <span className="mobile-row-date"><span>{isToday ? "Today" : date.format("ddd")}</span><strong>{date.format("MMM D")}</strong></span>
                        <span className="mobile-row-main">
                            {!schedule ? <><strong>Off</strong><span>No shift scheduled</span></>
                                : isTimeOffSchedule(schedule) ? <><strong>Time off</strong><span>No shift</span></>
                                    : <><strong>{formatTime(schedule.shiftStart)} – {formatTime(schedule.shiftEnd)}</strong><span>{getScheduleLocationLabel(schedule.location)}</span></>}
                        </span>
                    </>
                ));
            })}
        </div>
    );

    const renderTeamWeek = (weekDates: Dayjs[]) => (
        <div className="mobile-day-list">
            {weekDates.map((date) => {
                const dateString = date.format("YYYY-MM-DD");
                const isToday = date.isSame(dayjs(), "day");
                const dayShifts = getDayShifts(dateString);
                const workingCount = dayShifts.filter(({ schedule }) => !isTimeOffSchedule(schedule)).length;
                return (
                    <div key={dateString} className={`mobile-day${isToday ? " today" : ""}`}>
                        <div className="mobile-day-heading">
                            <strong>{date.format("dddd, MMM D")}</strong>
                            {isToday ? <span className="today-badge">Today</span> : null}
                            <span className="mobile-day-count">{workingCount} working</span>
                        </div>
                        {dayShifts.length ? dayShifts.map(({ employee, schedule }) => {
                            const timeOff = isTimeOffSchedule(schedule);
                            const isMe = employee.id === userData?.id;
                            const className = `mobile-shift-row ${getLocationVariant(schedule.location)}${isMe ? " is-me" : ""}`;
                            return renderMobileRow(employee.id, className, employee.id, dateString, (
                                <>
                                    <span className="mobile-row-main">
                                        <strong>{employee.firstName} {employee.lastName}{isMe ? " (you)" : ""}</strong>
                                        <span>{timeOff ? "Time off" : getScheduleLocationLabel(schedule.location)}</span>
                                    </span>
                                    {!timeOff ? <span className="mobile-row-time">{formatTime(schedule.shiftStart)} – {formatTime(schedule.shiftEnd)}</span> : null}
                                </>
                            ));
                        }) : <p className="mobile-day-empty">No one scheduled</p>}
                    </div>
                );
            })}
        </div>
    );

    const renderMonth = () => (
        <>
            <div className="week-heading">
                <div>
                    <h3>{workDates.length ? `${workDates[0].format("MMM D")} – ${workDates[workDates.length - 1].format("MMM D, YYYY")}` : "No dates selected"}</h3>
                    {userData?.admin ? <span>Click a shift to edit it</span> : null}
                </div>
                <div className="schedule-legend">
                    {LOCATION_LEGEND.map((item) => <span key={item.variant} className={item.variant}>{item.label}</span>)}
                </div>
            </div>
            <div className="month-calendar">
                {MONTH_WEEKDAYS.map((weekdayName) => <div key={weekdayName} className="month-weekday">{weekdayName}</div>)}
                {workDates.map((date, index) => {
                    const dateString = date.format("YYYY-MM-DD");
                    const isToday = date.isSame(dayjs(), "day");
                    const dayShifts = getDayShifts(dateString);
                    const workingCount = dayShifts.filter(({ schedule }) => !isTimeOffSchedule(schedule)).length;
                    return (
                        <div key={dateString} className={`month-day${isToday ? " today" : ""}`}>
                            <div className="month-day-heading">
                                <strong>{index === 0 || !date.isSame(workDates[index - 1], "month") ? date.format("MMM D") : date.format("D")}</strong>
                                {dayShifts.length ? <span>{workingCount} working</span> : null}
                            </div>
                            {dayShifts.map(({ employee, schedule }) => {
                                const timeOff = isTimeOffSchedule(schedule);
                                const details = `${employee.firstName} ${employee.lastName}: ${timeOff ? "Time off" : `${formatTime(schedule.shiftStart)} – ${formatTime(schedule.shiftEnd)}, ${getScheduleLocationLabel(schedule.location)}`}`;
                                const content = (
                                    <>
                                        <span className="month-shift-name">{employee.firstName} {employee.lastName.charAt(0)}.</span>
                                        {!timeOff ? <span className="month-shift-location">{getShortLocationLabel(schedule.location)}</span> : null}
                                        <span className="month-shift-time">{timeOff ? "Off" : formatCompactRange(schedule)}</span>
                                    </>
                                );
                                const className = `month-shift ${getLocationVariant(schedule.location)}`;
                                return userData?.admin ? (
                                    <button key={employee.id} type="button" className={className} title={details}
                                        onClick={() => openShiftEditor(employee.id, dateString)}>{content}</button>
                                ) : <div key={employee.id} className={className} title={details}>{content}</div>;
                            })}
                            {userData?.admin && scheduledEmployees.some((employee) => !employeeScheduleMap[employee.id]?.[dateString]) ? (
                                <button type="button" className="month-add-button" onClick={() => openNewShiftEditor(dateString)}>+ Add shift</button>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </>
    );

    const hasShiftsInRange = (employeeId: string) => Object.keys(employeeScheduleMap[employeeId] || {}).length > 0;

    // Hidden employees (e.g. contractors) still appear if they have a shift in the range, so no shift is ever hidden.
    const scheduledEmployees = employees.filter((employee) => !hiddenEmployeeIds.has(employee.id) || hasShiftsInRange(employee.id));

    const handleToggleScheduleVisibility = async (employeeId: string) => {
        if (!userData?.admin) return;
        const hideFromSchedule = !hiddenEmployeeIds.has(employeeId);
        const updateHiddenIds = (hide: boolean) => setHiddenEmployeeIds((currentIds) => {
            const nextIds = new Set(currentIds);
            if (hide) nextIds.add(employeeId); else nextIds.delete(employeeId);
            return nextIds;
        });
        updateHiddenIds(hideFromSchedule);
        try {
            await updateDoc(doc(db, "employees", employeeId), { hideFromSchedule });
        } catch (error) {
            console.error("Error updating schedule visibility:", error);
            updateHiddenIds(!hideFromSchedule);
            setMessage("Could not update who is shown");
        }
    };

    const editorEmployee = employees.find((employee) => employee.id === shiftEditor?.userId);

    return (
        <div className="scheduler-page">
            <Header />
            {userData?.admin ? (
                <div className="scheduler-tabs">
                    <Tabs value={tabIndex} onChange={(_event, value) => setTabIndex(value)} className="my-tabs"
                        TabIndicatorProps={{ style: { backgroundColor: "black" } }}>
                        <Tab label="Schedule" className="single-tab" />
                        <Tab label="Blackout days" className="single-tab" />
                    </Tabs>
                </div>
            ) : null}

            <div className="scheduler-toolbar">
                <div>
                    <p className="scheduler-eyebrow">Schedule range</p>
                    <h1>{tabIndex === 1 ? "Blackout days" : "Team schedule"}</h1>
                </div>
                <div className="scheduler-toolbar-controls">
                    {(!userData?.admin || tabIndex === 0) ? (
                        <div className="view-toggle" role="group" aria-label="Schedule view">
                            {viewOptions.map((view) => (
                                <button key={view.id} type="button" className={activeView === view.id ? "selected" : ""}
                                    aria-pressed={activeView === view.id} onClick={() => setScheduleView(view.id)}>{view.label}</button>
                            ))}
                        </div>
                    ) : null}
                    <DateRangePicker startDate={dateRange.startDate} endDate={dateRange.endDate} onChange={updateDateRange} />
                </div>
            </div>

            {(!userData?.admin || tabIndex === 0) ? (
                <main className="scheduler-content">
                    {userData?.admin && activeView === "week" ? (
                        <section className="template-toolbar" aria-label="Shift templates">
                            <div className="template-toolbar-heading">
                                <p className="scheduler-eyebrow">Paint mode</p>
                                <span className="template-help" title="Drag horizontally for one employee or vertically to give several employees the same day.">
                                    Pick a shift, then click or drag across the grid. Custom for unusual hours, Erase to clear.
                                </span>
                                <span className={`save-state${isSaving ? " saving" : ""}`}>{isSaving ? "Saving…" : "Changes auto-save"}</span>
                                <button className="copy-week-button" onClick={() => setShowPeopleEditor(true)}>
                                    People · {employees.length - hiddenEmployeeIds.size}/{employees.length} shown
                                </button>
                            </div>
                            <div className="template-list">
                                {shiftTemplates.map((template) => (
                                    <div key={template.id} className="template-item">
                                        <button className={`template-button ${template.mode === "shift" ? getLocationVariant(template.location) : template.id}${selectedTemplate.id === template.id ? " selected" : ""}`}
                                            aria-pressed={selectedTemplate.id === template.id} onClick={() => setSelectedTemplateId(template.id)}>
                                            <span className="template-swatch" />
                                            <span>
                                                <strong>{template.label}</strong>
                                                {template.saved ? <small>{formatTemplateRange(template.startTime, template.endTime)} · {getShortLocationLabel(template.location)}</small>
                                                    : template.mode === "shift" && !isTimeOffLocation(template.location) ? <small>{getScheduleLocationLabel(template.location)}</small> : null}
                                            </span>
                                        </button>
                                        {template.saved ? <button className="remove-template-button" aria-label={`Remove the ${template.label} button`}
                                            onClick={() => handleDeleteTemplate(template)}>×</button> : null}
                                    </div>
                                ))}
                                <button className="template-button new-template-button"
                                    onClick={() => setTemplateDraft({ label: "", location: TEMPLATE_LOCATIONS[0], startTime: "09:00", endTime: "17:30" })}>
                                    + New shift button
                                </button>
                            </div>
                        </section>
                    ) : null}

                    <section className="schedule-overview">
                        {isLoadingSchedule ? <span className="schedule-loading">Loading schedule…</span> : null}
                        {activeView === "month" ? <div className="schedule-weeks" aria-busy={isLoadingSchedule}>{renderMonth()}</div> : (
                            <div className="schedule-weeks" aria-busy={isLoadingSchedule}>
                                {scheduleWeeks.map((weekDates, weekIndex) => {
                                    if (!weekDates.length) return null;
                                    const weekStart = weekDates[0].subtract(1, "day");
                                    const weekEnd = weekDates[weekDates.length - 1];
                                    return (
                                        <section className="schedule-week" key={weekStart.format("YYYY-MM-DD")}>
                                            <div className="week-heading">
                                                <div><h3>Week of {weekStart.format("MMM D")}</h3><span>{weekStart.format("MMM D")} – {weekEnd.format("MMM D, YYYY")}</span></div>
                                                {activeView === "mine" && userData?.id ? <span className="week-hours-pill">{getWeekHours(userData.id, weekDates)} hrs</span> : null}
                                                {userData?.admin && weekIndex > 0 && activeView === "week" ? <button className="copy-week-button" disabled={isSaving} onClick={() => copyPreviousWeek(weekDates)}>Copy previous week</button> : null}
                                            </div>
                                            {activeView !== "week" ? (activeView === "mine" ? renderMyWeek(weekDates) : renderTeamWeek(weekDates)) : (
                                                <div className="schedule-grid-scroll">
                                                    <table className="schedule-grid">
                                                        <thead><tr>
                                                            <th className="employee-column">Employee</th>
                                                            {weekDates.map((date) => <th key={date.format("YYYY-MM-DD")} className={date.isSame(dayjs(), "day") ? "today-column" : ""}><span>{date.format("ddd")}</span><strong>{date.format("MMM D")}</strong></th>)}
                                                            <th className="hours-column">Hours</th>
                                                        </tr></thead>
                                                        <tbody>
                                                            {scheduledEmployees.map((employee) => (
                                                                <tr key={employee.id}>
                                                                    <th className="employee-column" scope="row"><strong>{employee.firstName} {employee.lastName}</strong></th>
                                                                    {weekDates.map((date) => {
                                                                        const dateString = date.format("YYYY-MM-DD");
                                                                        const schedule = employeeScheduleMap[employee.id]?.[dateString];
                                                                        const timeOff = schedule ? isTimeOffSchedule(schedule) : false;
                                                                        return (
                                                                            <td key={dateString} className={date.isSame(dayjs(), "day") ? "today-column" : ""}>
                                                                                <div className={getCellClassName(schedule)} title={schedule ? (timeOff ? "Time off" : `${formatTime(schedule.shiftStart)} – ${formatTime(schedule.shiftEnd)}, ${getScheduleLocationLabel(schedule.location)}`) : undefined} role={userData?.admin ? "button" : undefined} tabIndex={userData?.admin ? 0 : undefined}
                                                                                    onPointerDown={(event) => handleCellPointerDown(event, employee.id, dateString)}
                                                                                    onPointerEnter={(event) => handleCellPointerEnter(event, employee.id, dateString)}
                                                                                    onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); paintedCellsRef.current.clear(); applySelectedTool(employee.id, dateString); flushPaintOperations(); } }}>
                                                                                    {schedule ? (
                                                                                        <>
                                                                                            <strong>{timeOff ? "Time off" : formatCompactRange(schedule)}</strong>
                                                                                            {!timeOff ? <span>{getShortLocationLabel(schedule.location)}</span> : null}
                                                                                            {userData?.admin ? <button className="edit-cell-button" aria-label={`Edit ${employee.firstName}'s shift on ${date.format("MMMM D")}`}
                                                                                                onPointerDown={(event) => event.stopPropagation()} onClick={() => openShiftEditor(employee.id, dateString)}>Edit</button> : null}
                                                                                        </>
                                                                                    ) : <span className="empty-cell-label">{userData?.admin ? "+" : "—"}</span>}
                                                                                </div>
                                                                            </td>
                                                                        );
                                                                    })}
                                                                    <td className="hours-column"><strong>{getWeekHours(employee.id, weekDates)}</strong></td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </section>
                                    );
                                })}
                            </div>
                        )}
                    </section>
                </main>
            ) : null}

            {userData?.admin && tabIndex === 1 ? (
                <main className="scheduler-content blackout-content">
                    <div className="blackout-builder">
                        <div><p className="scheduler-eyebrow">PTO controls</p><h2>Add a blackout day</h2><p>Employees will be unable to request PTO on this date.</p></div>
                        <label className="schedule-label"><span>Date</span><input type="date" value={blackoutDate} onChange={(event) => setBlackoutDate(event.target.value)} /></label>
                        <button className="schedule-button" onClick={handleAddBlackoutDay}>Add blackout day</button>
                    </div>
                    <div className="blackout-list">
                        {blackoutDaysInRange.length === 0 ? <p className="blackout-empty">No blackout days in this range.</p> : blackoutDaysInRange.map((day) => (
                            <div key={day.id} className="blackout-card">
                                <div><span className="blackout-weekday">{dayjs(day.date).format("dddd")}</span><span className="blackout-date">{dayjs(day.date).format("MMM D, YYYY")}</span></div>
                                <button className="remove-blackout" onClick={() => handleRemoveBlackoutDay(day.date)}>Remove</button>
                            </div>
                        ))}
                    </div>
                </main>
            ) : null}

            {shiftEditor ? (
                <div className="shift-editor-backdrop" role="presentation" onMouseDown={() => !isSaving && setShiftEditor(null)}>
                    <section className="shift-editor" role="dialog" aria-modal="true" aria-labelledby="shift-editor-title" onMouseDown={(event) => event.stopPropagation()}>
                        <div className="shift-editor-heading">
                            <div><p className="scheduler-eyebrow">{shiftEditor.chooseEmployee ? "New shift" : "Custom shift"}</p><h2 id="shift-editor-title">{shiftEditor.chooseEmployee ? "Add a shift" : `${editorEmployee?.firstName} ${editorEmployee?.lastName}`}</h2><span>{dayjs(shiftEditor.date).format("dddd, MMMM D, YYYY")}</span></div>
                            <button className="close-editor-button" aria-label="Close shift editor" onClick={() => setShiftEditor(null)}>×</button>
                        </div>
                        {shiftEditor.chooseEmployee ? (
                            <label className="schedule-label employee-select"><span>Employee</span><select value={shiftEditor.userId} onChange={(event) => setShiftEditor({ ...shiftEditor, userId: event.target.value })}>
                                <option value="">Choose an employee…</option>
                                {scheduledEmployees.filter((employee) => !employeeScheduleMap[employee.id]?.[shiftEditor.date]).map((employee) => (
                                    <option key={employee.id} value={employee.id}>{employee.firstName} {employee.lastName}</option>
                                ))}
                            </select></label>
                        ) : null}
                        <div className="quick-fill">
                            <span>Quick fill</span>
                            {quickFillTemplates.map((template) => (
                                <button key={template.id} type="button" className={`quick-fill-button ${getLocationVariant(template.location)}`}
                                    onClick={() => setShiftEditor({ ...shiftEditor, location: template.location, startTime: template.startTime, endTime: template.endTime })}>
                                    {template.label}
                                </button>
                            ))}
                        </div>
                        <div className="shift-editor-fields">
                            <label className="schedule-label"><span>Location</span><select value={shiftEditor.location} onChange={(event) => setShiftEditor({ ...shiftEditor, location: event.target.value })}>
                                {SCHEDULE_LOCATIONS.map((location) => <option key={location} value={location}>{location}</option>)}
                            </select></label>
                            {shiftEditor.location !== "Time Off" ? <>
                                <label className="schedule-label"><span>Starts</span><input type="time" value={shiftEditor.startTime} onChange={(event) => setShiftEditor({ ...shiftEditor, startTime: event.target.value })} /></label>
                                <label className="schedule-label"><span>Ends</span><input type="time" value={shiftEditor.endTime} onChange={(event) => setShiftEditor({ ...shiftEditor, endTime: event.target.value })} /></label>
                            </> : null}
                        </div>
                        <p className="overnight-help">An end time earlier than the start time is saved as an overnight shift.</p>
                        <div className="shift-editor-actions">
                            {shiftEditor.hasExistingShift ? <button className="delete-shift-button" disabled={isSaving} onClick={handleEditorDelete}>Delete shift</button> : <span />}
                            <div><button className="cancel-button" disabled={isSaving} onClick={() => setShiftEditor(null)}>Cancel</button><button className="schedule-button" disabled={isSaving || !shiftEditor.userId} onClick={handleEditorSave}>{isSaving ? "Saving…" : "Save shift"}</button></div>
                        </div>
                    </section>
                </div>
            ) : null}

            {showPeopleEditor ? (
                <div className="shift-editor-backdrop" role="presentation" onMouseDown={() => setShowPeopleEditor(false)}>
                    <section className="shift-editor" role="dialog" aria-modal="true" aria-labelledby="people-editor-title" onMouseDown={(event) => event.stopPropagation()}>
                        <div className="shift-editor-heading">
                            <div><p className="scheduler-eyebrow">Week grid</p><h2 id="people-editor-title">Who shows on the schedule</h2><span>Uncheck people who are never scheduled, like contractors. This applies for every admin.</span></div>
                            <button className="close-editor-button" aria-label="Close" onClick={() => setShowPeopleEditor(false)}>×</button>
                        </div>
                        <div className="people-list">
                            {employees.map((employee) => {
                                const hidden = hiddenEmployeeIds.has(employee.id);
                                return (
                                    <label key={employee.id} className="people-row">
                                        <input type="checkbox" checked={!hidden} onChange={() => handleToggleScheduleVisibility(employee.id)} />
                                        <span>{employee.firstName} {employee.lastName}</span>
                                        {hidden && hasShiftsInRange(employee.id) ? <small>Still shown: has shifts in these dates</small> : null}
                                    </label>
                                );
                            })}
                        </div>
                        <div className="shift-editor-actions">
                            <span />
                            <button className="schedule-button" onClick={() => setShowPeopleEditor(false)}>Done</button>
                        </div>
                    </section>
                </div>
            ) : null}

            {templateDraft ? (
                <div className="shift-editor-backdrop" role="presentation" onMouseDown={() => !isSaving && setTemplateDraft(null)}>
                    <section className="shift-editor" role="dialog" aria-modal="true" aria-labelledby="template-editor-title" onMouseDown={(event) => event.stopPropagation()}>
                        <div className="shift-editor-heading">
                            <div><p className="scheduler-eyebrow">Paint mode</p><h2 id="template-editor-title">New shift button</h2><span>Saved buttons are shared with every admin.</span></div>
                            <button className="close-editor-button" aria-label="Close shift button editor" onClick={() => setTemplateDraft(null)}>×</button>
                        </div>
                        <label className="schedule-label employee-select"><span>Button name (optional)</span>
                            <input type="text" maxLength={30} value={templateDraft.label}
                                placeholder={`${formatTemplateRange(templateDraft.startTime || "09:00", templateDraft.endTime || "17:30")} ${getShortLocationLabel(templateDraft.location)}`}
                                onChange={(event) => setTemplateDraft({ ...templateDraft, label: event.target.value })} />
                        </label>
                        <div className="shift-editor-fields">
                            <label className="schedule-label"><span>Location</span><select value={templateDraft.location} onChange={(event) => setTemplateDraft({ ...templateDraft, location: event.target.value })}>
                                {TEMPLATE_LOCATIONS.map((location) => <option key={location} value={location}>{location}</option>)}
                            </select></label>
                            <label className="schedule-label"><span>Starts</span><input type="time" value={templateDraft.startTime} onChange={(event) => setTemplateDraft({ ...templateDraft, startTime: event.target.value })} /></label>
                            <label className="schedule-label"><span>Ends</span><input type="time" value={templateDraft.endTime} onChange={(event) => setTemplateDraft({ ...templateDraft, endTime: event.target.value })} /></label>
                        </div>
                        <p className="overnight-help">An end time earlier than the start time is saved as an overnight shift.</p>
                        <div className="shift-editor-actions">
                            <span />
                            <div><button className="cancel-button" disabled={isSaving} onClick={() => setTemplateDraft(null)}>Cancel</button><button className="schedule-button" disabled={isSaving} onClick={handleSaveTemplate}>{isSaving ? "Saving…" : "Save button"}</button></div>
                        </div>
                    </section>
                </div>
            ) : null}

            <Snackbar open={message !== ""} message={message} autoHideDuration={2200} onClose={() => setMessage("")}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
        </div>
    );
};

require("./styles.scss");
